import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { TranslationNS_Admin } from '@gokrew/models/constants';
import { Input } from '@gokrew/ui';
import { ComboBox } from '@gokrew/ui';
import { addCrmPartner } from '@/api/crm';
import type { CrmPartner } from '@/api/crm';
import { CrmButton, CrmRequired } from './CrmShared';
import styles from './Crm.module.css';

export const CrmPartnerForm = ({
  onAdded,
}: {
  onAdded: (partner: CrmPartner) => void;
}) => {
  const { t } = useTranslation(TranslationNS_Admin.CompanyInfo);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [kind, setKind] = useState('broker');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(false);
  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (busy || !name.trim()) return;
    setBusy(true);
    setError(false);
    try {
      const partner = await addCrmPartner({
        name: name.trim(),
        kind,
        ...(email.trim() ? { email: email.trim() } : {}),
      });
      onAdded(partner);
      setName('');
      setEmail('');
    } catch {
      setError(true);
    } finally {
      setBusy(false);
    }
  };
  return (
    <form onSubmit={submit} className={styles.form}>
      <label>
        <span>
          {t('crm.partner_name')}
          <CrmRequired />
        </span>
        <Input
          required
          maxLength={200}
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
      </label>
      <label>
        {t('crm.type')}
        <ComboBox
          allowUnselect={false}
          value={kind}
          options={['broker', 'hr_consultant'].map((value) => ({
            value,
            label: t(`crm.sources.${value}`),
          }))}
          onChange={(value) => {
            if (value) setKind(value);
          }}
        />
      </label>
      <label>
        {t('crm.email')}
        <Input
          type="email"
          maxLength={254}
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
      </label>
      {error && <p role="alert">{t('crm.save_error')}</p>}
      <CrmButton
        type="submit"
        variant="adminPrimary"
        disabled={busy || !name.trim()}
      >
        {t(busy ? 'crm.saving' : 'crm.add_partner')}
      </CrmButton>
    </form>
  );
};
